import { useState, useEffect, useCallback } from "react";
import { userPool } from "./config/auth";
import { ROLE_PERMISSIONS } from "./config/roles";
import { apiGetProducts, apiPostTransaction } from "./services/api";
import LoginPage from "./components/LoginPage/LoginPage";
import HomePage from "./pages/HomePage/HomePage";
import InventoryPage from "./pages/InventoryPage/InventoryPage";
import ActivityPage from "./pages/ActivityPage/ActivityPage";
import DashboardPage from "./pages/DashboardPage/DashboardPage";
import BarcodesPage from "./pages/BarcodesPage/BarcodesPage";
import styles from "./App.module.css";

export default function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("home");
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const canAccess = useCallback((feature) => {
    if (!user) return false;
    return ROLE_PERMISSIONS[user.role]?.includes(feature) ?? false;
  }, [user]);

  const handleLogin = useCallback((u) => {
    setUser(u);
    setPage(ROLE_PERMISSIONS[u.role]?.includes("home") ? "home" : "inventory");
  }, []);

  const handleLogout = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setUser(null);
    setPage("home");
  }, []);

  const fetchProducts = useCallback(() => {
    if (!user) return;
    apiGetProducts(user.token)
      .then(data => {
        if (data && data.products) {
          setProducts(data.products);
        }
      })
      .catch(err => console.error("Failed to load products from API:", err));
  }, [user]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleNavClick = useCallback((id) => {
    setPage(id);
    if (id === "inventory" || id === "dashboard" || id === "barcodes") {
      fetchProducts();
    }
  }, [fetchProducts]);

  const handleTransaction = useCallback(async (tx, productId, newStock) => {
    setTransactions(prev => [tx, ...prev]);
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, stock: newStock } : p));
    try {
      await apiPostTransaction(user.token, tx);
    } catch (err) {
      console.error("Failed to save transaction to API:", err);
      setTransactions(prev => prev.filter(t => t.id !== tx.id));
      // Rollback: IN adds stock so rollback subtracts, OUT/WORK_ORDER/DELIVERY subtract so rollback adds
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, stock: p.stock + (tx.type === "IN" ? -tx.quantity : tx.quantity) } : p));
      alert("Transaction failed to save. Please try again.");
    }
  }, [user]);

  const handleNewProduct = useCallback((product) => {
    setProducts(prev => [...prev, product]);
  }, []);

  const handleUpdateProduct = useCallback((oldProductId, updatedProduct) => {
    setProducts(prev => {
      // If the ID changed (lot number edit), remove old and add new
      if (oldProductId !== updatedProduct.id) {
        return [...prev.filter(p => p.id !== oldProductId), updatedProduct];
      }
      return prev.map(p => p.id === oldProductId ? { ...p, ...updatedProduct } : p);
    });
  }, []);

  if (!user) return <LoginPage onLogin={handleLogin} />;

  const navItems = [
    { id: "home",      label: "Home",      show: canAccess("home") },
    { id: "inventory", label: "Inventory", show: canAccess("inventory") },
    { id: "barcodes",  label: "Barcodes",  show: canAccess("barcodes") },
    { id: "activity",  label: "Activity",  show: canAccess("activity") },
    { id: "dashboard", label: "Dashboard", show: canAccess("dashboard") },
  ].filter(n => n.show);

  const drugCount  = products.filter(p => p.type === "Drug").length;
  const supplyCount = products.filter(p => p.type === "Supply").length;
  const totalStock  = products.reduce((s, p) => s + (p.stock || 0), 0);
  const zeroStock   = products.filter(p => (p.stock || 0) === 0).length;

  const stats = [
    { label: "Total Products", value: products.length,             color: "var(--accent-cyan)" },
    { label: "Drugs",          value: drugCount,                   color: "var(--accent-orange)" },
    { label: "Supplies",       value: supplyCount,                 color: "var(--accent-purple)" },
    { label: "Total Stock",    value: totalStock.toLocaleString(), color: "var(--accent-green)" },
    { label: "Zero Stock",     value: zeroStock,                   color: zeroStock > 0 ? "var(--accent-red)" : "var(--text-dim)" },
  ];

  return (
    <div className={styles.app}>
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.brand}>
            <div className={styles.brandText}>Vital Care - Inventory</div>
          </div>
          <nav className={styles.nav}>
            {navItems.map(n => (
              <button key={n.id} onClick={() => handleNavClick(n.id)}
                className={page === n.id ? styles.navBtnActive : styles.navBtn}>
                {n.label}
              </button>
            ))}
          </nav>
          <div className={styles.userSection}>
            <div className={styles.avatar}>{user.initials}</div>
            <div className={styles.role}>{user.role}</div>
            <button onClick={handleLogout} className={styles.logoutBtn}>LOGOUT</button>
          </div>
        </div>
      </div>

      <div className={styles.statsBar}>
        <div className={styles.statsInner}>
          {stats.map(s => (
            <div key={s.label} className={styles.stat}>
              <div className={styles.statValue} style={{ color: s.color }}>{s.value}</div>
              <div className={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.main}>
        {page === "home"      && canAccess("home")      && <HomePage products={products} user={user} onTransaction={handleTransaction} onNewProduct={handleNewProduct} onUpdateProduct={handleUpdateProduct} />}
        {page === "inventory" && canAccess("inventory") && <InventoryPage products={products} onNewProduct={handleNewProduct} canAccess={canAccess} user={user} />}
        {page === "activity"  && canAccess("activity")  && <ActivityPage transactions={transactions} />}
        {page === "barcodes"  && canAccess("barcodes")  && <BarcodesPage products={products} />}
        {page === "dashboard" && canAccess("dashboard") && <DashboardPage user={user} products={products} />}
      </div>
    </div>
  );
}
